import type { WorkingView } from './working-progress';

// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * WHAT THE SETUP SCREEN SAYS, FROM WHAT THE ENGINE REPORTS (owner-approved, October 3, 2026).
 *
 * The right side of a new job's setup is one picture per step, the step's own count,
 * one plain live line and the results so far as tiles; a two-part bar on top shows
 * that learning (from the samples) comes first and reading every file comes after.
 * This turns the one progress state (working-progress.ts) into that view and nothing
 * else: no number here is invented, and every word is the customer's, not the engine's.
 *
 * The screen names five stages, in order, for what the customer gets out of each:
 * Reading pages (the engine's Prepare and Read), Separating documents, Finding
 * document types (the list, then each document matched to one), Choosing fields,
 * and Reading your documents. That last stage is ONE stage across two engine runs:
 * setup trying its fields on the documents it learned from, then (after setup is
 * saved) every file read with what it learned. On a processor setup learns from the
 * whole folder, so both runs read the same files; shown as two counts, the screen
 * climbed to "97 of 100" and then started again at "42 of 100" (owner, October 10,
 * 2026: "the user thinks they're about done, then it jumps back past halfway"). So
 * the stage shows one share of both reads, weighted by how much each reads, which
 * only rises (readingShare). It offers "Ready to check" once the first batch is in,
 * and gives way to the job when every file is read.
 */

export type JourneyArt =
  'reading' | 'separating' | 'types' | 'fields' | 'values' | 'files' | 'done';

export type JourneyTile = {
  id: 'pages' | 'documents' | 'types' | 'fields' | 'values';
  label: string;
  value: number | null;
};

export type JourneyType = {
  name: string;
  shade: number;
  fields: string[];
  state: 'ready' | 'working' | 'waiting';
};

export type JourneyModel = {
  phase: 'learning' | 'saving' | 'reading' | 'ready';
  /** The two-part bar: how far learning is (0..1) and how far reading every file is (0..1). */
  bar: {
    learn: number;
    read: number;
    learnDone: boolean;
    readDone: boolean;
    readCount: number | null;
    total: number | null;
    /** Where "Start checking" sits on the reading part (0..1), or null when unknown. */
    mark: number | null;
    readLeft: string | null;
  };
  hero: {
    art: JourneyArt;
    step: string;
    value: string | null;
    of: string | null;
    live: string | null;
    left: string | null;
  };
  tiles: JourneyTile[];
  /** Index of the learning part (0..4) while learning, 5 once learned. */
  part: number;
  /**
   * The one count this moment is about, as numbers (the hero's while learning; the reading
   * stage's share as a percent of 100, from setup's own read to the last file): never lower
   * than shown before within a part (working-progress.ts).
   */
  count: { value: number; total: number } | null;
};

/** The learning parts, in order. One name each, used everywhere. */
export const READING_STAGE = 'Reading your documents';
export const LEARNING_PARTS = [
  'Reading pages',
  'Separating documents',
  'Finding document types',
  'Choosing fields',
  READING_STAGE,
] as const;
const PART_ART: JourneyArt[] = ['reading', 'separating', 'types', 'fields', 'files'];

/** How much of the two-part bar learning takes: the same proportion as the setup choice draws. */
export const LEARN_SHARE = 0.23;
/** The parts "Initial learning" (the bar's first half) covers; the last part, reading, fills its second half. */
const LEARN_PARTS = LEARNING_PARTS.length - 1;

/** The whole bar as one number (0..1): the release gate proves it never falls. */
export const overallBar = (bar: Pick<JourneyModel['bar'], 'learn' | 'read'>) =>
  LEARN_SHARE * clamp(bar.learn) + (1 - LEARN_SHARE) * clamp(bar.read);

/**
 * How much of the reading stage setup's own read is: the documents it tries its fields on,
 * against those plus every file read after it. Fixed once setup's read is counted, so the
 * share below never moves backwards when the second read starts.
 */
export function setupReadWeight(setupTotal: number, files: number | null | undefined): number {
  const setup = Math.max(0, Number.isFinite(setupTotal) ? setupTotal : 0);
  const all = Math.max(0, files && Number.isFinite(files) ? files : 0);
  return setup + all > 0 ? setup / (setup + all) : 0;
}

/** The reading stage as one share (0..1): setup's read, then every file, never lower than shown before. */
export function readingShare(input: {
  weight: number;
  setup: number;
  files: number;
  floor?: number;
}): number {
  const weight = clamp(input.weight);
  return Math.max(
    clamp(input.floor ?? 0),
    clamp(weight * clamp(input.setup) + (1 - weight) * clamp(input.files)),
  );
}

/** "37%": the reading stage's one number. Floored, so it says 100% only when every file is read. */
export const percent = (share: number) => `${Math.floor(clamp(share) * 100)}%`;

/**
 * What the screen remembers to say time left honestly: the step it was measuring, where its
 * count stood when first seen, and the last estimate shown (a countdown between estimates).
 */
export type LeftMemory = {
  key: string;
  firstAt: number;
  firstDone: number;
  measured: number | null;
  at: number;
  left: number | null;
};

/**
 * Time left for the step on screen, recalibrating as it goes. "About 1 min" stayed up for five
 * minutes (owner, October 10, 2026): the estimate came from the engine's last event, so between
 * events it was the same number however long the step took. Now:
 * - a NEW measured estimate (one that differs from the last) is used as it is;
 * - otherwise the step's own pace on this screen (time since its count was first seen, per item
 *   done since), which grows while the count stands still, once three items are done;
 * - otherwise the last estimate counts down with the clock, and once it has run out the screen
 *   says nothing rather than a time it has already overrun.
 * `perItem` is the measured time per item, for pricing work still to come.
 */
export function timeLeft(
  memory: LeftMemory | null,
  input: {
    key: string;
    now: number;
    done: number | null;
    total: number | null;
    measured?: number | null;
  },
): { memory: LeftMemory; milliseconds: number | null; perItem: number | null } {
  const done = input.done !== null && Number.isFinite(input.done) ? Math.max(0, input.done) : null;
  let next: LeftMemory =
    memory && memory.key === input.key
      ? { ...memory }
      : {
          key: input.key,
          firstAt: input.now,
          firstDone: done ?? 0,
          measured: null,
          at: input.now,
          left: null,
        };
  if (done !== null && done < next.firstDone)
    next = { ...next, firstAt: input.now, firstDone: done };
  const measured =
    input.measured && Number.isFinite(input.measured) && input.measured > 0 ? input.measured : null;
  const items = done !== null ? done - next.firstDone : 0;
  const spent = input.now - next.firstAt;
  const perItem = items >= 3 && spent > 0 ? spent / items : null;
  const pace =
    perItem !== null && input.total && done !== null && done < input.total
      ? perItem * (input.total - done)
      : null;
  const fresh = measured !== null && measured !== next.measured ? measured : null;
  next.measured = measured;
  const estimate = fresh ?? pace;
  if (estimate !== null)
    return { memory: { ...next, at: input.now, left: estimate }, milliseconds: estimate, perItem };
  const countdown = next.left !== null ? next.left - (input.now - next.at) : null;
  return {
    memory: next,
    milliseconds: countdown !== null && countdown > 0 ? countdown : null,
    perItem,
  };
}

const clamp = (value: number) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
const fraction = (completed: number, total: number) => (total > 0 ? clamp(completed / total) : 0);
const plural = (value: number, one: string, many: string) =>
  `${value.toLocaleString()} ${value === 1 ? one : many}`;

/** "about 3 min" / "about 40 sec"; null below three samples (the screen never prices from one). */
export function aboutLeft(milliseconds: number | null | undefined): string | null {
  if (!milliseconds || !Number.isFinite(milliseconds) || milliseconds <= 0) return null;
  const minutes = Math.round(milliseconds / 60_000);
  if (minutes >= 1) return `About ${minutes} min`;
  return `About ${Math.max(5, Math.round(milliseconds / 5_000) * 5)} sec`;
}

/**
 * The engine's named waits (ProgressStream phases) in the customer's words. A long step
 * is still shown working; its name never takes over the screen, it only fills the live line.
 */
const PHASES: Record<string, string> = {
  'Loading the setup model': 'Getting the AI ready',
  'Measuring the samples': 'Measuring your samples',
  'Finishing the field lists': 'Finishing the field lists',
  'Reading the documents that did not finish': 'Going back for a few documents',
  'Handing the pages to the document reader': 'Getting ready to read',
  'Starting the page comparer': 'Getting ready to separate',
  'Grouping pages into documents': 'Grouping pages into documents',
  'Starting the document reader': 'Getting ready',
  'Starting the page reader': 'Getting ready to read',
  'Comparing document types': 'Comparing document types',
  'Finishing the document types': 'Finishing the document types',
  'Checking pages for missing page markers': 'Checking page order',
  'Checking types and values against similar documents': 'Checking against similar documents',
  'Saving the batch': 'Saving',
  'Applying what you confirmed before': 'Using what you confirmed before',
};
export const plainPhase = (text: string) => PHASES[text.trim()] ?? text.trim();

/**
 * A live line as the customer reads it. The barcode kind is a technical name
 * ("Code 39"); the value is what they care about.
 */
export function plainLive(text: string | null | undefined): string | null {
  const line = (text ?? '').trim();
  if (!line) return null;
  const barcode = /^Found barcode .+? · (.+)$/.exec(line);
  if (barcode) return `Found a barcode · ${barcode[1]}`;
  const found = /^(\d[\d,]*) documents? found in these pages$/.exec(line);
  if (found) return `${found[1]} ${found[1] === '1' ? 'document' : 'documents'} found`;
  if (line === 'Finding where each document starts') return 'Finding where each document starts';
  if (line === 'Preparing the files') return 'Opening your files';
  return plainPhase(line);
}

/** The setup step's own activity label, in the customer's words. */
function setupLive(
  view: WorkingView,
  now: number,
  questionLine: (now: number) => string | null,
): string | null {
  if (view.question) return questionLine(now);
  const label = (view.activityLabel ?? '').trim();
  if (view.stepId === 'type-list') {
    if (label === 'Organizing document types') return 'Tidying the list of types';
    return 'Comparing your documents';
  }
  if (view.stepId === 'type-assignment') return 'Matching documents to types';
  if (view.stepId === 'fields') {
    const choosing = /^Choosing useful fields for (.+)$/.exec(label);
    if (choosing) return `Choosing fields for ${choosing[1]}`;
    const chose = /^(?:Chose fields for (.+)|(.+) fields ready)$/.exec(label);
    if (chose) return `${chose[1] ?? chose[2]} is ready`;
    return 'Choosing fields';
  }
  const last = view.reading.lastDocument;
  if (last)
    return `${last.type}${last.values[0] ? ` · ${last.values[0].name} ${last.values[0].value}` : ''}`;
  return 'Reading the fields';
}

export type LearningInput = {
  view: WorkingView;
  now: number;
  /** The step clock's estimate for the part being done (workingReadout), or null. */
  remainingMilliseconds: number | null;
  /** The question line for a reasoner choosing fields, already in the customer's words. */
  questionLine: (now: number) => string | null;
  /** The document types named so far, with their fields once chosen. */
  types: JourneyType[];
  /** The furthest the learning bar has been shown, so it never slides back. */
  learnFloor: number;
  /** True while accepting the proposal or checking the destination. */
  saving: boolean;
  checkingDestination?: boolean;
  /**
   * The engine re-reading beneath the furthest point already shown (after Try again, or a
   * restart, a setup can start over from the first page): how far it has got again, and the
   * point it is catching up to; done is null when it is still in an earlier step.
   */
  catchUp?: { done: number | null; to: number } | null;
  /** Files in the whole selection (read in full once setup is saved), when known. */
  files?: number | null;
  /** The furthest the reading stage has been shown (0..1), so it never slides back. */
  readFloor?: number;
  /**
   * How much of the reading stage setup's own read is (setupReadWeight), as the caller fixed it
   * while that read was counted; the files' read after saving carries on from exactly here.
   */
  setupWeight?: number;
};

/** The learning part (0..4) a view is in, and how far through it (0..1). */
export function learningPart(view: WorkingView): { part: number; within: number } {
  switch (view.stageId) {
    case 'preparing':
      return { part: 0, within: 0.25 * fraction(view.completed, view.total) };
    case 'recognizing':
      return { part: 0, within: 0.25 + 0.75 * fraction(view.completed, view.total) };
    case 'organizing':
      return { part: 1, within: fraction(view.completed, view.total) };
    case 'understanding': {
      if (view.stepId === 'type-list')
        return { part: 2, within: 0.5 * fraction(view.completed, view.total) };
      if (view.stepId === 'type-assignment')
        return { part: 2, within: 0.5 + 0.5 * fraction(view.completed, view.total) };
      if (view.stepId === 'fields')
        return { part: 3, within: fraction(view.completed, view.total) };
      if (view.stepId === 'values')
        return { part: 4, within: fraction(view.completed, view.total) };
      // Understanding before its first setup step (the readers starting) is the start of finding types.
      return view.counted && view.completed > 0
        ? { part: 4, within: fraction(view.completed, view.total) }
        : { part: 2, within: 0 };
    }
    default:
      return { part: 4, within: 1 };
  }
}

/** The results so far: a tile turns from an outline into a number when its part is done. */
export function learningTiles(
  view: WorkingView,
  part: number,
  types: readonly JourneyType[],
  done = false,
): JourneyTile[] {
  const fieldCount = types.reduce(
    (sum, type) => sum + (type.state === 'ready' ? type.fields.length : 0),
    0,
  );
  return [
    { id: 'pages', label: 'pages', value: part >= 1 && view.pageCount > 0 ? view.pageCount : null },
    {
      id: 'documents',
      label: 'documents',
      value: part >= 2 && view.documentCount > 0 ? view.documentCount : null,
    },
    // The types are counted only once saved: while setup learns, two names can still become one
    // ("6 document types" showed, then 5 were saved: owner, October 10, 2026).
    { id: 'types', label: 'document types', value: done && types.length > 0 ? types.length : null },
    {
      id: 'fields',
      label: 'fields',
      value: (part >= 4 || done) && fieldCount > 0 ? fieldCount : null,
    },
    { id: 'values', label: 'values', value: done && view.fieldCount > 0 ? view.fieldCount : null },
  ];
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function learningModel(input: LearningInput): JourneyModel {
  const { view, now, types } = input;
  const { part, within } = learningPart(view);
  // Initial learning is the first four parts; the reading stage fills the bar's second half.
  const learn =
    part >= LEARN_PARTS
      ? 1
      : Math.max(input.learnFloor, clamp((part + clamp(within)) / LEARN_PARTS));
  const bar = {
    learn,
    read: 0,
    learnDone: part >= LEARN_PARTS,
    readDone: false,
    readCount: null,
    total: null,
    mark: null,
    readLeft: null,
  };
  const files = input.files && input.files > 0 ? input.files : null;
  const filesOf = files ? `of ${plural(files, 'file', 'files')}` : null;
  if (input.saving || view.stageId === 'finalizing') {
    // Saving sits inside the reading stage: setup's own read is done, the files' read is next.
    // The stage, its number and the bar hold; only the live line says what is happening.
    const share = Math.max(clamp(input.readFloor ?? 0), clamp(input.setupWeight ?? 0));
    return {
      phase: 'saving',
      part: 5,
      count: { value: Math.floor(share * 100), total: 100 },
      bar: { ...bar, learn: 1, learnDone: true, read: share },
      hero: {
        art: 'files',
        step: READING_STAGE,
        value: percent(share),
        of: filesOf,
        live: input.checkingDestination ? 'Checking the setup' : 'Saving what it learned',
        left: null,
      },
      tiles: learningTiles(view, 5, types, true),
    };
  }
  if (part >= LEARN_PARTS) {
    // Setup's own read of its documents: the first stretch of the one reading stage.
    const weight = Math.max(
      clamp(input.setupWeight ?? 0),
      view.counted && view.stepId === 'values' ? setupReadWeight(view.total, files) : 0,
    );
    const share = readingShare({
      weight,
      setup: view.counted ? fraction(view.completed, view.total) : 0,
      files: 0,
      floor: input.readFloor,
    });
    let live =
      view.counted || view.stepId ? setupLive(view, now, input.questionLine) : 'Getting ready';
    if (view.phaseText) live = plainPhase(view.phaseText);
    if (input.catchUp) live = 'Catching up to where it stopped';
    const left = view.question ? null : aboutLeft(input.remainingMilliseconds);
    return {
      phase: 'learning',
      part,
      bar: { ...bar, read: share },
      count: { value: Math.floor(share * 100), total: 100 },
      hero: {
        art: PART_ART[part],
        step: READING_STAGE,
        value: percent(share),
        of: filesOf,
        live,
        left: left ? `${left} for all` : null,
      },
      tiles: learningTiles(view, part, types),
    };
  }
  let value: string | null = null;
  let of: string | null = null;
  let live: string | null = null;
  // Two parts begin with an engine step of their own count (opening the files before their
  // pages are read; comparing the documents before each is matched to a type). That first
  // count rides in the live line, so the big number never starts over within a part.
  const quietCount = view.counted
    ? ` · ${view.completed.toLocaleString()} of ${view.total.toLocaleString()}`
    : '';
  if (view.stageId === 'preparing') {
    const label = (view.activityLabel ?? '').trim();
    live =
      view.activityId === 'straighten'
        ? /^Checking (\d[\d,]*) pages? for skew$/.test(label)
          ? 'Making sure every page is straight'
          : label || 'Making sure every page is straight'
        : `Opening your files${quietCount}`;
  } else if (view.stageId === 'understanding' && view.stepId === 'type-list') {
    live = `${setupLive(view, now, input.questionLine)}${quietCount}`;
  } else if (view.stageId === 'recognizing') {
    if (view.counted) {
      value = view.completed.toLocaleString();
      of = `of ${plural(view.total, 'page', 'pages')}`;
    }
    live = plainLive(view.subStep);
  } else if (view.stageId === 'organizing') {
    if (view.counted) {
      value = view.completed.toLocaleString();
      of = `of ${plural(view.total, 'page', 'pages')}`;
    }
    live = plainLive(view.subStep);
  } else if (view.stepId) {
    if (view.counted) {
      value = view.completed.toLocaleString();
      of =
        view.stepId === 'fields'
          ? `of ${plural(view.total, 'document type', 'document types')}`
          : view.unit.includes('tables')
            ? `of ${view.total.toLocaleString()} documents and tables`
            : `of ${plural(view.total, 'document', 'documents')}`;
    }
    live = setupLive(view, now, input.questionLine);
  } else {
    live = view.counted && view.completed > 0 ? plainLive(view.subStep) : 'Getting ready';
    if (view.counted && view.completed > 0) {
      value = view.completed.toLocaleString();
      of = `of ${plural(view.total, 'document', 'documents')}`;
    }
  }
  // A heartbeat names the long wait it is in: the live line says it plainly, the step stays.
  if (view.phaseText) live = plainPhase(view.phaseText);
  // The count holds at the furthest point shown (it never goes back), so the live line says
  // the engine is catching up beneath it; it looked frozen at "100 of 212" for minutes after
  // Try again (seen October 3, 2026).
  if (input.catchUp)
    live =
      input.catchUp.done !== null
        ? `Catching up to where it stopped · ${input.catchUp.done.toLocaleString()} of ${input.catchUp.to.toLocaleString()}`
        : 'Catching up to where it stopped';
  // Never the step's own name again beneath it.
  if (live && live.toLocaleLowerCase() === LEARNING_PARTS[part].toLocaleLowerCase()) live = null;
  // The step's own time, said as the step's: never read as the whole job's time left.
  const left = view.question ? null : aboutLeft(input.remainingMilliseconds);
  return {
    phase: 'learning',
    part,
    bar,
    count: value !== null ? { value: view.completed, total: view.total } : null,
    hero: {
      art: PART_ART[part],
      step: LEARNING_PARTS[part],
      value,
      of,
      live,
      left: left ? `${left} for this step` : null,
    },
    tiles: learningTiles(view, part, types),
  };
}

export type ReadingInput = {
  /** Files in the whole selection (the import's total). */
  total: number;
  /** Files read so far, never fewer than shown before. */
  read: number;
  live: string | null;
  remainingMilliseconds: number | null;
  /** Documents in the job, ready to check. */
  documents: number;
  /** Files in the first batch (the import's first two waves), when known. */
  firstBatchFiles: number | null;
  /** The first batch is in the job. */
  firstBatchReady: boolean;
  /**
   * How much of the reading stage setup's own read was (setupReadWeight), already done; 0 when
   * setup read nothing of its own (or an older hand-off did not record it).
   */
  setupWeight?: number;
  /** The reading share this setup has already shown (setup-progress-floor.ts): the files' read never draws it shorter. */
  readFloor?: number;
};

/** After setup is saved: every file is read with what it learned, the rest of the one reading stage. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function readingModel(input: ReadingInput): JourneyModel {
  const total = Math.max(0, input.total);
  const read = Math.min(total, Math.max(0, input.read));
  const ready = input.firstBatchReady && input.documents > 0;
  const left = aboutLeft(input.remainingMilliseconds);
  const weight = clamp(input.setupWeight ?? 0);
  const share = readingShare({
    weight,
    setup: 1,
    files: fraction(read, total),
    floor: input.readFloor,
  });
  const readDone = total > 0 && read >= total;
  return {
    phase: ready ? 'ready' : 'reading',
    part: 5,
    count: { value: Math.floor(share * 100), total: 100 },
    bar: {
      learn: 1,
      read: share,
      learnDone: true,
      readDone,
      readCount: read,
      total,
      mark:
        input.firstBatchFiles && total > 0
          ? clamp(weight + ((1 - weight) * input.firstBatchFiles) / total)
          : null,
      readLeft: left,
    },
    hero: ready
      ? {
          art: 'done',
          step: 'Ready to check',
          value: input.documents.toLocaleString(),
          of: input.documents === 1 ? 'document' : 'documents',
          live: null,
          left: null,
        }
      : {
          art: 'files',
          step: READING_STAGE,
          value: percent(share),
          of: `of ${plural(total, 'file', 'files')}`,
          // Every file read (its values in, folder-import-progress.ts) but the job not open yet: the
          // status bar's words for the same moment, never a bare "10 of 10" that looks stuck.
          live:
            plainLive(input.live) ??
            (read === 0
              ? 'Starting with the first files'
              : total > 0 && read >= total
                ? 'Finishing your files'
                : null),
          left: left ? `${left} for all` : null,
        },
    tiles: [],
  };
}
